import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ArrowLeftOutline,
	Box,
	Button,
	Center,
	Checkbox,
	ChevronRightOutline,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PlusOutline,
	Portal,
	rem,
	SearchOutline,
	Text,
	useMenuContext,
	VStack,
} from '@bonusly/design-system';

import { AttrMap, CustomPropertyUserGroups, ruleSectionKeyToConditionAttr, useCustomPropertyUserGroups } from '../../queries/groups';
import { titleCase } from '../../utils/string';
import { Reveal } from '../display/reveal';
import { AudienceCondition } from './condition-audience-selector';

type InternalFilters = Record<string, string[]>;

const SUB_FILTER_SEARCH_MIN_COUNT = 10;

const toInternalFilters = (conditions: AudienceCondition[]): InternalFilters => {
	const result: InternalFilters = {};
	for (const c of conditions) {
		if (c.attr && c.value) {
			const sectionKey = AttrMap[c.attr] ?? c.attr;
			result[sectionKey] = result[sectionKey] || [];
			if (!result[sectionKey].includes(c.value)) {
				result[sectionKey].push(c.value);
			}
		}
	}
	return result;
};

const propertyLabel = (sectionKey: string, t: (key: string, opts?: Record<string, string>) => string): string => {
	const context = ruleSectionKeyToConditionAttr(sectionKey);
	return t('audienceLabel', { context, fallback: context.replaceAll('_', ' ') });
};

interface FiltersMenuContentProps {
	groupData: CustomPropertyUserGroups;
	propertyKeys: string[];
	currentConditions: AudienceCondition[];
	onApply: (filters: InternalFilters) => void;
	isOpen: boolean;
	variant: 'include' | 'exclude';
	translationNamespace: string;
	translationKeyPrefix: string;
}

const FiltersMenuContent: FC<FiltersMenuContentProps> = ({
	groupData,
	propertyKeys,
	currentConditions,
	onApply,
	isOpen,
	variant,
	translationNamespace,
	translationKeyPrefix,
}) => {
	const { t } = useTranslation(translationNamespace, { keyPrefix: translationKeyPrefix });
	const { t: tShared } = useTranslation('common', { keyPrefix: 'audienceSelector' });
	const { onClose } = useMenuContext();
	const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
	const [localFilters, setLocalFilters] = useState<InternalFilters>({});
	const [localPropertyOptions, setLocalPropertyOptions] = useState<string[]>([]);
	const [searchText, setSearchText] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const cancelButtonRef = useRef<HTMLButtonElement>(null);
	const subFilterCancelButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		setLocalFilters(toInternalFilters(currentConditions));
		setSelectedProperty(null);
		setLocalPropertyOptions([]);
		setSearchText('');
	}, [currentConditions, isOpen]);

	useEffect(() => {
		if (isOpen) {
			const timer = setTimeout(() => {
				const firstButton = listRef.current?.querySelector('button');
				firstButton?.focus();
			}, 50);
			return () => clearTimeout(timer);
		}
	}, [isOpen, selectedProperty]);

	const handlePropertyClick = (propertyName: string) => {
		setSelectedProperty(propertyName);
		setLocalPropertyOptions(localFilters[propertyName] || []);
		setSearchText('');
	};

	const handleApply = () => {
		const cleaned = Object.fromEntries(Object.entries(localFilters).filter(([, values]) => values.length > 0));
		onApply(cleaned);
		onClose();
	};

	const handleCancel = () => {
		onClose();
	};

	const handleOptionToggle = (optionKey: string) => {
		setLocalPropertyOptions((prev) => (prev.includes(optionKey) ? prev.filter((key) => key !== optionKey) : [...prev, optionKey]));
	};

	const handleSubFilterDone = () => {
		if (selectedProperty) {
			const newFilters = { ...localFilters };
			if (localPropertyOptions.length === 0) {
				delete newFilters[selectedProperty];
			} else {
				newFilters[selectedProperty] = localPropertyOptions;
			}
			setLocalFilters(newFilters);
		}
		setSelectedProperty(null);
		setLocalPropertyOptions([]);
		setSearchText('');
	};

	const handleSubFilterCancel = () => {
		setSelectedProperty(null);
		setLocalPropertyOptions([]);
		setSearchText('');
	};

	const propertyOptions = selectedProperty ? groupData[selectedProperty] || [] : [];
	const showSubfilterSearch = propertyOptions.length > SUB_FILTER_SEARCH_MIN_COUNT;

	const filteredPropertyOptions = useMemo(() => {
		if (!showSubfilterSearch) return propertyOptions;
		const query = searchText.toLowerCase().trim();
		return propertyOptions.filter((opt) => opt.name.toLowerCase().includes(query));
	}, [propertyOptions, searchText, showSubfilterSearch]);

	const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Tab' && !e.shiftKey) {
			e.preventDefault();
			if (selectedProperty) {
				subFilterCancelButtonRef.current?.focus();
			} else {
				cancelButtonRef.current?.focus();
			}
		}
	};

	const handleFooterKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Tab' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
			e.stopPropagation();
		}
	};

	if (!selectedProperty) {
		return (
			<>
				<Box borderBottom="1px solid" borderBottomColor="border.default">
					<Text px={4} py={3} fontSize={200} fontWeight="semibold">
						{variant === 'exclude' ? tShared('excludeModalTitle') : tShared('includeModalTitle')}
					</Text>
				</Box>
				<Box ref={listRef} maxH={rem(355)} w={rem(320)} overflowY="auto" onKeyDown={handleListKeyDown}>
					<VStack spacing={0} align="stretch">
						{propertyKeys.map((propertyName) => (
							<MenuItem
								key={propertyName}
								minHeight={rem(54)}
								px={5}
								py={3}
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								borderBottom="1px solid"
								borderBottomColor="border.default"
								borderRadius={0}
								_hover={{ bg: 'bg.canvas' }}
								_focus={{ bg: 'bg.canvas' }}
								_focusVisible={{ bg: 'bg.canvas', outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
								onClick={() => handlePropertyClick(propertyName)}
								closeOnSelect={false}
								data-testid={`filter-property-${propertyName}`}
							>
								<Text fontSize={100} fontWeight="semibold" textTransform="capitalize">
									{propertyLabel(propertyName, t)}
								</Text>
								<HStack spacing={2}>
									<Reveal if={(localFilters[propertyName]?.length || 0) > 0}>
										<Box w={6} h={6} borderRadius="full" bg="blue.50" display="flex" alignItems="center" justifyContent="center">
											<Text fontSize={100} textAlign="center">
												{localFilters[propertyName]?.length || 0}
											</Text>
										</Box>
									</Reveal>
									<ChevronRightOutline size="sm" />
								</HStack>
							</MenuItem>
						))}
					</VStack>
				</Box>
				<HStack justifyContent="space-between" width="100%" px={4} py={3} borderTop="1px solid" borderTopColor="border.default" onKeyDown={handleFooterKeyDown}>
					<Button ref={cancelButtonRef} variant="alpha" colorScheme="secondary" onClick={handleCancel}>
						{tShared('filterCancelButton')}
					</Button>
					<Button colorScheme="primary" onClick={handleApply}>
						{tShared('filterApplyButton')}
					</Button>
				</HStack>
			</>
		);
	}

	return (
		<>
			<HStack px={4} py={3} borderBottom="1px solid" borderBottomColor="border.default" spacing={2} onKeyDown={handleFooterKeyDown}>
				<IconButton aria-label={tShared('filterBackButton')} icon={<ArrowLeftOutline />} variant="ghost" size="sm" onClick={handleSubFilterDone} />
				<Text fontSize={200} fontWeight="semibold" textTransform="capitalize">
					{propertyLabel(selectedProperty, t)}
				</Text>
			</HStack>
			{showSubfilterSearch && (
				<Box px={4} py={3} borderBottom="1px solid" borderBottomColor="border.default">
					<InputGroup size="sm">
						<InputLeftElement>
							<SearchOutline size="xs" />
						</InputLeftElement>
						<Input
							ref={searchInputRef}
							placeholder={tShared('filterSearchPlaceholder')}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							autoComplete="off"
							borderRadius="full"
							data-testid="sub-filter-search"
							isInvalid={false}
						/>
					</InputGroup>
				</Box>
			)}
			<Box ref={listRef} maxH={rem(310)} w={rem(320)} overflowY="auto" onKeyDown={handleListKeyDown}>
				<VStack spacing={0} align="stretch">
					{filteredPropertyOptions.length === 0 && (
						<Center py={4}>
							<Text color="text.softer" fontSize={100}>
								{tShared('filterSearchNoResults')}
							</Text>
						</Center>
					)}
					{filteredPropertyOptions.map((option) => {
						const shouldCapitalize = selectedProperty === 'departments' || selectedProperty === 'locations';
						const displayName = shouldCapitalize ? titleCase(option.name) : option.name;

						return (
							<MenuItem
								key={option.key}
								minHeight={rem(54)}
								px={5}
								py={3}
								display="flex"
								justifyContent="space-between"
								borderBottom="1px solid"
								borderBottomColor="border.default"
								borderRadius={0}
								_hover={{ bg: 'bg.canvas' }}
								_focus={{ bg: 'bg.canvas' }}
								_focusVisible={{ bg: 'bg.canvas', outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
								onClick={() => handleOptionToggle(option.key)}
								closeOnSelect={false}
								data-testid={`sub-filter-option-${option.key}`}
							>
								<Text fontSize={100} textAlign="left">
									{displayName}
								</Text>
								<Checkbox isChecked={localPropertyOptions.includes(option.key)} pointerEvents="none" tabIndex={-1} aria-hidden="true" isInvalid={false} />
							</MenuItem>
						);
					})}
				</VStack>
			</Box>
			<HStack justifyContent="space-between" width="100%" px={4} py={3} borderTop="1px solid" borderTopColor="border.default" onKeyDown={handleFooterKeyDown}>
				<Button ref={subFilterCancelButtonRef} variant="alpha" colorScheme="secondary" onClick={handleSubFilterCancel}>
					{tShared('filterCancelButton')}
				</Button>
				<Button colorScheme="primary" onClick={handleSubFilterDone}>
					{tShared('filterDoneButton')}
				</Button>
			</HStack>
		</>
	);
};

interface AudienceFiltersMenuProps {
	currentConditions: AudienceCondition[];
	hasFilters: boolean;
	onApply: (filters: InternalFilters) => void;
	variant?: 'include' | 'exclude';
	translationNamespace?: string;
	translationKeyPrefix?: string;
}

export const AudienceFiltersMenu: FC<AudienceFiltersMenuProps> = ({
	currentConditions,
	hasFilters,
	onApply,
	variant = 'include',
	translationNamespace = 'nominations',
	translationKeyPrefix = 'form',
}) => {
	const { t: tShared } = useTranslation('common', { keyPrefix: 'audienceSelector' });
	const { data: groupData } = useCustomPropertyUserGroups();
	const [isOpen, setIsOpen] = useState(false);

	const propertyKeys = useMemo(() => {
		if (!groupData) return [];
		return Object.keys(groupData).filter((key) => (groupData[key] ?? []).length > 0);
	}, [groupData]);

	const buttonText = variant === 'exclude' ? tShared('excludeButtonText') : tShared('filterButtonText');
	const testId = variant === 'exclude' ? 'add-exclude-button' : 'add-filter-button';

	if (!groupData || propertyKeys.length === 0) return null;

	return (
		<Menu placement="bottom-start" closeOnSelect={false} isOpen={isOpen} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)} offset={[0, 2]}>
			{hasFilters ? (
				<MenuButton as={Button} variant="alpha" colorScheme="secondary" size="sm" h={7} w={7} p={0} data-testid={testId} aria-label={buttonText}>
					<PlusOutline size="xs" pt={1} />
				</MenuButton>
			) : (
				<MenuButton as={Button} variant="link" colorScheme="primary" size="sm" leftIcon={<PlusOutline size="xs" />} data-testid={testId}>
					{buttonText}
				</MenuButton>
			)}
			<Portal>
				<MenuList p={0} minW={rem(280)} zIndex="popover">
					<FiltersMenuContent
						groupData={groupData}
						propertyKeys={propertyKeys}
						currentConditions={currentConditions}
						onApply={onApply}
						isOpen={isOpen}
						variant={variant}
						translationNamespace={translationNamespace}
						translationKeyPrefix={translationKeyPrefix}
					/>
				</MenuList>
			</Portal>
		</Menu>
	);
};

export type { InternalFilters };
